import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, setSession } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { Mail, Timer, RefreshCw, X } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';

export default function VerificationOTP() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(sessionStorage.getItem('bfi_otp_challenge') || '');
  const [maskedEmail, setMaskedEmail] = useState(sessionStorage.getItem('bfi_otp_email') || '');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [timeLeft, setTimeLeft] = useState((parseInt(sessionStorage.getItem('bfi_otp_validity') || '10', 10)) * 60);
  const inputs = useRef([]);

  useEffect(() => {
    if (!challenge) navigate('/connexion');
    inputs.current[0]?.focus();
    const i = setInterval(() => {
      setTimeLeft((t) => Math.max(t - 1, 0));
      setCooldown((c) => Math.max(c - 1, 0));
    }, 1000);
    return () => clearInterval(i);
  }, [challenge, navigate]);

  const setDigit = (idx, val) => {
    const v = val.replace(/[^0-9]/g, '').slice(-1);
    const arr = [...code]; arr[idx] = v; setCode(arr);
    if (v && idx < 5) inputs.current[idx + 1]?.focus();
  };
  const onKey = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };
  const onPaste = (e) => {
    const text = (e.clipboardData?.getData('text') || '').replace(/[^0-9]/g, '').slice(0, 6);
    if (text.length === 6) {
      e.preventDefault();
      setCode(text.split(''));
      inputs.current[5]?.focus();
    }
  };

  const verify = async () => {
    const c = code.join('');
    if (c.length !== 6) { toast.error(t('auth.enterAllDigits')); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/otp/verify', { challenge, code: c });
      if (data.step === 'done') {
        setSession(data.token, data.user);
        sessionStorage.removeItem('bfi_otp_challenge');
        sessionStorage.removeItem('bfi_otp_email');
        toast.success(t('auth.loginSuccess'));
        navigate('/dashboard');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('auth.wrongOtp'));
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      const { data } = await api.post('/auth/otp/resend', { challenge });
      setChallenge(data.challenge);
      sessionStorage.setItem('bfi_otp_challenge', data.challenge);
      setCooldown(60);
      setTimeLeft((parseInt(sessionStorage.getItem('bfi_otp_validity') || '10', 10)) * 60);
      toast.success(data.message || t('auth.otpSent'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('common.operationError')); }
  };

  const cancel = () => { sessionStorage.clear(); navigate('/connexion'); };

  const fmt = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
        <div className="flex items-center justify-between">
          <Logo />
          <button onClick={cancel} className="text-slate-500 hover:text-slate-700" data-testid="btn-otp-cancel"><X className="w-5 h-5"/></button>
        </div>
        <div className="mt-6 mx-auto w-14 h-14 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center"><Mail className="w-7 h-7"/></div>
        <h1 className="text-2xl font-bold text-center mt-4">{t('auth.identityVerification')}</h1>
        <p className="text-center text-slate-600 mt-2 text-sm">{t('auth.otpSubtitle')}<br/><span className="font-medium text-slate-900">{maskedEmail}</span></p>
        <div className="mt-7 flex justify-center gap-2" onPaste={onPaste}>
          {code.map((c, i) => (
            <input key={i} ref={(el) => inputs.current[i] = el} value={c} onChange={(e) => setDigit(i, e.target.value)} onKeyDown={(e) => onKey(i, e)}
              maxLength={1} inputMode="numeric" autoComplete="one-time-code"
              className="w-12 h-14 border-2 border-slate-200 rounded-xl text-center text-2xl font-bold focus:border-blue-700 focus:outline-none transition"
              data-testid={`input-otp-${i}`}/>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500"><Timer className="w-4 h-4"/> {t('auth.codeValid')} {fmt(timeLeft)}</div>
        <Button className="mt-6 w-full bg-blue-700 hover:bg-blue-800" onClick={verify} disabled={loading || code.join('').length < 6} data-testid="btn-otp-verify">
          {loading ? t('auth.verifying') : t('auth.verify')}
        </Button>
        <div className="mt-3 text-center">
          <button onClick={resend} disabled={cooldown > 0} className={`text-sm font-medium inline-flex items-center gap-1 ${cooldown > 0 ? 'text-slate-400' : 'text-blue-700 hover:underline'}`} data-testid="btn-otp-resend">
            <RefreshCw className="w-4 h-4"/> {t('auth.resendOtp')} {cooldown > 0 ? `(${cooldown}s)` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
